import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
/* eslint-disable jsdoc/require-jsdoc */
import { Button, Drawer, Input, Message, Select, Tabs, Tag } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CheckCircle2, Pencil, Plus, Save, Upload } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import {
  supplierAttachmentDefaultType,
  supplierAttachmentTypeOptions,
  supplierContactRoleOptions,
  supplierTimedCooperationStatusOptions,
  supplierTimedReviewStatusOptions,
} from "../config";
import {
  createSupplierAttachment,
  createSupplierBankAccount,
  createSupplierBusCapability,
  createSupplierContact,
  createSupplierGuideRelation,
  disableSupplierBankAccount,
  disableSupplierContact,
  fetchSupplierAttachments,
  fetchSupplierBankAccounts,
  fetchSupplierBusCapabilities,
  fetchSupplierContacts,
  fetchSupplierGuideRelations,
  fetchGuideProviderOptions,
  fetchSupplierQuoteCapability,
  fetchSupplierStayCapabilities,
  obsoleteSupplierAttachment,
  reviewSupplierAttachment,
  saveSupplierQuoteCapability,
  updateSupplierAttachment,
  updateSupplierBankAccount,
  updateSupplierBusCapability,
  updateSupplierContact,
  updateSupplierGuideRelation,
  uploadSupplierMedia,
} from "../repo/supplierBasicRepo";
import type {
  SupplierAttachmentMutationInput,
  SupplierBankAccountMutation,
  SupplierBusCapabilityMutation,
  SupplierBusCapabilityRecord,
  SupplierContactMutationInput,
  SupplierContactRecord,
  SupplierGuideRelationMutation,
  GuideProviderOption,
  SupplierItem,
  SupplierKind,
  SupplierQuoteCapabilityMutation,
} from "../types";
import "./SupplyBasicPage.css";

type SupplierDetailDrawerProps = {
  kind: SupplierKind;
  item: SupplierItem;
  canManageContact: boolean;
  canManageAccount: boolean;
  canManageAttachment: boolean;
  canManageCapability: boolean;
  onClose: () => void;
};

const emptyContact = (): SupplierContactMutationInput => ({
  contactName: "",
  contactRoleType: "operation",
  remark: "",
});
const emptyAccount = (): SupplierBankAccountMutation => ({
  accountName: "",
  bankName: "",
  bankAccountNo: "",
  accountType: "public",
  usageType: "payee",
  isDefaultPayee: false,
  isDefaultRefundAccount: false,
  remark: "",
});
const emptyAttachment = (): SupplierAttachmentMutationInput => ({
  attachmentType: supplierAttachmentDefaultType,
  attachmentName: "",
  fileUrl: "",
  remark: "",
});
const emptyQuote = (): SupplierQuoteCapabilityMutation => ({
  canQuoteHotel: false,
  canQuoteBus: false,
  canQuoteTicket: false,
  canQuoteGuide: false,
  canQuoteMeal: false,
  isEnabled: true,
  remark: "",
});
const emptyBusCapability = (): SupplierBusCapabilityMutation => ({
  cooperationStatus: "candidate",
  reviewStatus: "pending",
  remark: "",
});
const emptyGuideRelation = (supplierId: string): SupplierGuideRelationMutation => ({
  providerSupplierId: "",
  guideSupplierId: supplierId,
  relationType: "employment",
  cooperationStatus: "candidate",
  reviewStatus: "pending",
  remark: "",
});

/** Independent supplier child records and coarse capability management. */
export function SupplierDetailDrawer(props: SupplierDetailDrawerProps) {
  const { item, kind } = props;
  const supplierId = item.supplierId;
  const queryClient = useQueryClient();
  const queryPrefix = ["supply-basic-settings", "supplier-detail", supplierId] as const;
  const [guideProviderSearch, setGuideProviderSearch] = useState("");

  const contactsQuery = useQuery({
    queryKey: [...queryPrefix, "contacts"],
    queryFn: () => fetchSupplierContacts(supplierId),
  });
  const accountsQuery = useQuery({
    queryKey: [...queryPrefix, "accounts"],
    queryFn: () => fetchSupplierBankAccounts(supplierId),
  });
  const attachmentsQuery = useQuery({
    queryKey: [...queryPrefix, "attachments"],
    queryFn: () => fetchSupplierAttachments(supplierId),
  });
  const quoteQuery = useQuery({
    queryKey: [...queryPrefix, "quote"],
    queryFn: () => fetchSupplierQuoteCapability(supplierId),
  });
  const busQuery = useQuery({
    queryKey: [...queryPrefix, "bus-capabilities"],
    queryFn: () => fetchSupplierBusCapabilities(supplierId),
    enabled: kind === "buses",
  });
  const guideQuery = useQuery({
    queryKey: [...queryPrefix, "guide-relations"],
    queryFn: () => fetchSupplierGuideRelations(supplierId),
    enabled: kind === "guides",
  });
  const guideProviderQuery = useQuery({
    queryKey: [...queryPrefix, "guide-provider-options", guideProviderSearch],
    queryFn: () => fetchGuideProviderOptions(supplierId, guideProviderSearch),
    enabled: kind === "guides",
  });
  const stayQuery = useQuery({
    queryKey: [...queryPrefix, "stay-capabilities"],
    queryFn: () => fetchSupplierStayCapabilities(supplierId),
    enabled: kind === "hotels",
  });

  const invalidate = (leaf: string) =>
    queryClient.invalidateQueries({ queryKey: [...queryPrefix, leaf] });

  const [contactDraft, setContactDraft] = useState<SupplierContactMutationInput | null>(null);
  const [contactId, setContactId] = useState<string>();
  const [accountDraft, setAccountDraft] = useState<SupplierBankAccountMutation | null>(null);
  const [accountId, setAccountId] = useState<string>();
  const [attachmentDraft, setAttachmentDraft] =
    useState<SupplierAttachmentMutationInput | null>(null);
  const [attachmentId, setAttachmentId] = useState<string>();
  const [quoteDraft, setQuoteDraft] = useState<SupplierQuoteCapabilityMutation>(emptyQuote());
  const [busDraft, setBusDraft] = useState<SupplierBusCapabilityMutation | null>(null);
  const [busCapabilityId, setBusCapabilityId] = useState<string>();
  const [guideDraft, setGuideDraft] = useState<SupplierGuideRelationMutation | null>(null);
  const [guideRelationId, setGuideRelationId] = useState<string>();
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (quoteQuery.data) setQuoteDraft({ ...quoteQuery.data });
    else if (quoteQuery.isSuccess) setQuoteDraft(emptyQuote());
  }, [quoteQuery.data, quoteQuery.isSuccess]);

  const contactSave = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: SupplierContactMutationInput }) =>
      id
        ? updateSupplierContact(supplierId, id, payload)
        : createSupplierContact(supplierId, payload),
    onSuccess: async () => {
      await invalidate("contacts");
      setContactDraft(null);
      setContactId(undefined);
      Message.success("联系人已保存");
    },
    onError: showMutationError,
  });
  const contactDisable = useMutation({
    mutationFn: (id: string) => disableSupplierContact(supplierId, id),
    onSuccess: () => invalidate("contacts"),
    onError: showMutationError,
  });
  const accountSave = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: SupplierBankAccountMutation }) =>
      id
        ? updateSupplierBankAccount(supplierId, id, payload)
        : createSupplierBankAccount(supplierId, payload),
    onSuccess: async () => {
      await invalidate("accounts");
      setAccountDraft(null);
      setAccountId(undefined);
      Message.success("银行账户已保存");
    },
    onError: showMutationError,
  });
  const accountDisable = useMutation({
    mutationFn: (id: string) => disableSupplierBankAccount(supplierId, id),
    onSuccess: () => invalidate("accounts"),
    onError: showMutationError,
  });
  const attachmentSave = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: SupplierAttachmentMutationInput }) =>
      id
        ? updateSupplierAttachment(supplierId, id, payload)
        : createSupplierAttachment(supplierId, payload),
    onSuccess: async () => {
      await invalidate("attachments");
      setAttachmentDraft(null);
      setAttachmentId(undefined);
      Message.success("附件已提交审核");
    },
    onError: showMutationError,
  });
  const attachmentStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "valid" | "expired" | "obsolete" }) =>
      status === "obsolete"
        ? obsoleteSupplierAttachment(supplierId, id)
        : reviewSupplierAttachment(supplierId, id, status),
    onSuccess: () => invalidate("attachments"),
    onError: showMutationError,
  });
  const quoteSave = useMutation({
    mutationFn: (payload: SupplierQuoteCapabilityMutation) =>
      saveSupplierQuoteCapability(supplierId, payload),
    onSuccess: () => {
      void invalidate("quote");
      Message.success("报价能力已保存");
    },
    onError: showMutationError,
  });
  const busSave = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: SupplierBusCapabilityMutation }) =>
      id
        ? updateSupplierBusCapability(supplierId, id, payload)
        : createSupplierBusCapability(supplierId, payload),
    onSuccess: async () => {
      await invalidate("bus-capabilities");
      setBusDraft(null);
      setBusCapabilityId(undefined);
      Message.success("车队服务能力已保存");
    },
    onError: showMutationError,
  });
  const guideSave = useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: SupplierGuideRelationMutation }) =>
      id
        ? updateSupplierGuideRelation(supplierId, id, payload)
        : createSupplierGuideRelation(supplierId, payload),
    onSuccess: async () => {
      await invalidate("guide-relations");
      setGuideDraft(null);
      setGuideRelationId(undefined);
      Message.success("导游提供关系已保存");
    },
    onError: showMutationError,
  });

  return (
    <Drawer
      className="supply-basic-detail-drawer"
      title={`${item.supplierName} · 供应商详情`}
      visible
      width={820}
      placement="right"
      unmountOnExit
      focusLock={false}
      onCancel={props.onClose}
      footer={null}
    >
      <div className="supply-basic-detail-summary">
        <span>{item.supplierCode}</span>
        <Tag color={item.isDisabled ? "orangered" : "green"}>{item.isDisabledLabel}</Tag>
        <span>{[item.province, item.city].filter(Boolean).join(" / ") || "未设置区域"}</span>
      </div>
      <Tabs defaultActiveTab="contacts" destroyOnHide={false}>
        <Tabs.TabPane key="contacts" title={`联系人 ${contactsQuery.data?.length ?? 0}`}>
          <SectionToolbar
            label="联系人"
            canManage={props.canManageContact}
            onAdd={() => {
              setContactId(undefined);
              setContactDraft(emptyContact());
            }}
          />
          {contactDraft ? (
            <ContactEditor
              value={contactDraft}
              saving={contactSave.isPending}
              onChange={setContactDraft}
              onCancel={() => setContactDraft(null)}
              onSave={() => {
                if (!contactDraft.contactName.trim()) {
                  Message.warning("请填写联系人姓名");
                  return;
                }
                contactSave.mutate({ id: contactId, payload: contactDraft });
              }}
            />
          ) : null}
          <DataTable
            headers={["姓名", "角色", "联系方式", "默认用途", "状态", "操作"]}
            empty={contactsQuery.isLoading ? "正在加载联系人..." : "暂无联系人"}
          >
            {(contactsQuery.data ?? []).map((contact) => (
              <tr key={contact.supplierContactId || `${contact.contactName}-${contact.mobile}`}>
                <td>{contact.contactName}</td>
                <td>{optionLabel(supplierContactRoleOptions, contact.contactRoleType)}</td>
                <td>{[contact.mobile, contact.phone, contact.email].filter(Boolean).join(" / ") || "-"}</td>
                <td>{contactDefaultLabels(contact).join("、") || "-"}</td>
                <td><StatusTag value={contact.contactStatus} /></td>
                <td><RowActions>{props.canManageContact && !contact.isLegacy ? (
                  <>
                    <IconButton label="编辑联系人" icon={<Pencil size={15} />} onClick={() => {
                      setContactId(contact.supplierContactId);
                      setContactDraft({ ...contact });
                    }} />
                    {contact.contactStatus === "active" && contact.supplierContactId ? (
                      <IconButton label="停用联系人" icon={<Ban size={15} />} onClick={() => contactDisable.mutate(contact.supplierContactId!)} />
                    ) : null}
                  </>
                ) : <span className="supply-basic-muted">只读</span>}</RowActions></td>
              </tr>
            ))}
          </DataTable>
        </Tabs.TabPane>

        <Tabs.TabPane key="accounts" title={`银行账户 ${accountsQuery.data?.length ?? 0}`}>
          <SectionToolbar label="银行账户" canManage={props.canManageAccount} onAdd={() => {
            setAccountId(undefined);
            setAccountDraft(emptyAccount());
          }} />
          {accountDraft ? (
            <AccountEditor
              value={accountDraft}
              editing={Boolean(accountId)}
              saving={accountSave.isPending}
              onChange={setAccountDraft}
              onCancel={() => setAccountDraft(null)}
              onSave={() => {
                if (!accountDraft.accountName.trim() || !accountDraft.bankName.trim() || accountDraft.bankAccountNo.trim().length < 4) {
                  Message.warning("请完整填写户名、开户行和账户号");
                  return;
                }
                accountSave.mutate({ id: accountId, payload: accountDraft });
              }}
            />
          ) : null}
          <DataTable headers={["户名", "开户行", "账户号", "用途", "状态", "操作"]} empty={accountsQuery.isLoading ? "正在加载银行账户..." : "暂无银行账户"}>
            {(accountsQuery.data ?? []).map((account) => (
              <tr key={account.supplierBankAccountId}>
                <td>{account.accountName}</td>
                <td>{[account.bankName, account.bankBranchName].filter(Boolean).join(" / ")}</td>
                <td>{account.bankAccountNoMasked}</td>
                <td>{account.usageType}</td>
                <td><StatusTag value={account.accountStatus} /></td>
                <td><RowActions>{props.canManageAccount ? (
                  <>
                    <IconButton label="编辑银行账户" icon={<Pencil size={15} />} onClick={() => {
                      setAccountId(account.supplierBankAccountId);
                      setAccountDraft({ ...account, bankAccountNo: "" });
                    }} />
                    {account.accountStatus === "active" ? (
                      <IconButton label="停用银行账户" icon={<Ban size={15} />} onClick={() => accountDisable.mutate(account.supplierBankAccountId)} />
                    ) : null}
                  </>
                ) : <span className="supply-basic-muted">只读</span>}</RowActions></td>
              </tr>
            ))}
          </DataTable>
        </Tabs.TabPane>

        <Tabs.TabPane key="attachments" title={`附件 ${attachmentsQuery.data?.length ?? 0}`}>
          <SectionToolbar label="附件" canManage={props.canManageAttachment} onAdd={() => {
            setAttachmentId(undefined);
            setAttachmentDraft(emptyAttachment());
          }} />
          {attachmentDraft ? (
            <AttachmentEditor
              value={attachmentDraft}
              saving={attachmentSave.isPending || uploading}
              onChange={setAttachmentDraft}
              onCancel={() => setAttachmentDraft(null)}
              onUpload={async (file) => {
                setUploading(true);
                try {
                  const fileUrl = await uploadSupplierMedia(
                    file,
                    file.type.startsWith("image/") ? "image" : "file",
                  );
                  setAttachmentDraft((current) => current ? { ...current, fileUrl } : current);
                  Message.success("文件已上传");
                } catch (error) {
                  showMutationError(error);
                } finally {
                  setUploading(false);
                }
              }}
              onSave={() => {
                if (!attachmentDraft.attachmentName.trim() || !attachmentDraft.fileUrl.trim()) {
                  Message.warning("请填写附件名称并上传文件");
                  return;
                }
                attachmentSave.mutate({ id: attachmentId, payload: attachmentDraft });
              }}
            />
          ) : null}
          <DataTable headers={["名称", "类型", "有效期", "审核状态", "来源", "操作"]} empty={attachmentsQuery.isLoading ? "正在加载附件..." : "暂无附件"}>
            {(attachmentsQuery.data ?? []).map((attachment) => (
              <tr key={attachment.supplierAttachmentId || attachment.fileUrl}>
                <td><a href={resolveHttpResourceUrl(attachment.fileUrl)} target="_blank" rel="noreferrer">{attachment.attachmentName}</a></td>
                <td>{optionLabel(supplierAttachmentTypeOptions, attachment.attachmentType)}</td>
                <td>{[attachment.effectiveFrom, attachment.effectiveTo].filter(Boolean).join(" 至 ") || "-"}</td>
                <td><StatusTag value={attachment.attachmentStatus} /></td>
                <td>{attachment.isLegacy ? "历史同步" : "人工维护"}</td>
                <td><RowActions>{props.canManageAttachment && !attachment.isLegacy && attachment.supplierAttachmentId ? (
                  <>
                    <IconButton label="编辑附件" icon={<Pencil size={15} />} onClick={() => {
                      setAttachmentId(attachment.supplierAttachmentId);
                      setAttachmentDraft({ ...attachment });
                    }} />
                    {attachment.attachmentStatus === "pending_review" ? (
                      <IconButton label="审核通过" icon={<CheckCircle2 size={15} />} onClick={() => attachmentStatus.mutate({ id: attachment.supplierAttachmentId!, status: "valid" })} />
                    ) : null}
                    {attachment.attachmentStatus !== "obsolete" ? (
                      <IconButton label="作废附件" icon={<Ban size={15} />} onClick={() => attachmentStatus.mutate({ id: attachment.supplierAttachmentId!, status: "obsolete" })} />
                    ) : null}
                  </>
                ) : <span className="supply-basic-muted">只读</span>}</RowActions></td>
              </tr>
            ))}
          </DataTable>
        </Tabs.TabPane>

        <Tabs.TabPane key="quote" title="报价能力">
          <QuoteEditor
            value={quoteDraft}
            canManage={props.canManageCapability}
            saving={quoteSave.isPending}
            onChange={setQuoteDraft}
            onSave={() => quoteSave.mutate(quoteDraft)}
          />
        </Tabs.TabPane>

        {kind === "buses" ? (
          <Tabs.TabPane key="bus-capabilities" title={`车队服务能力 ${busQuery.data?.length ?? 0}`}>
            <SectionToolbar label="车队服务能力" canManage={props.canManageCapability} onAdd={() => {
              setBusCapabilityId(undefined);
              setBusDraft(emptyBusCapability());
            }} />
            {busDraft ? <BusCapabilityEditor value={busDraft} saving={busSave.isPending} onChange={setBusDraft} onCancel={() => setBusDraft(null)} onSave={() => busSave.mutate({ id: busCapabilityId, payload: busDraft })} /> : null}
            <DataTable headers={["服务区域", "车型/座位", "服务方式", "合作状态", "审核", "操作"]} empty={busQuery.isLoading ? "正在加载服务能力..." : "暂无车队服务能力"}>
              {(busQuery.data ?? []).map((capability) => (
                <tr key={capability.supplierBusServiceCapabilityId}>
                  <td>{[capability.province, capability.city, capability.district].filter(Boolean).join(" / ") || "-"}</td>
                  <td>{[capability.vehicleType, seatText(capability)].filter(Boolean).join(" / ") || "-"}</td>
                  <td>{capability.serviceMode || "-"}</td>
                  <td><StatusTag value={capability.cooperationStatus} /></td>
                  <td>{statusLabel(capability.reviewStatus)}</td>
                  <td><RowActions>{props.canManageCapability ? <IconButton label="编辑车队服务能力" icon={<Pencil size={15} />} onClick={() => {
                    setBusCapabilityId(capability.supplierBusServiceCapabilityId);
                    setBusDraft({ ...capability });
                  }} /> : <span className="supply-basic-muted">只读</span>}</RowActions></td>
                </tr>
              ))}
            </DataTable>
          </Tabs.TabPane>
        ) : null}

        {kind === "guides" ? (
          <Tabs.TabPane key="guide-relations" title={`提供关系 ${guideQuery.data?.length ?? 0}`}>
            <SectionToolbar label="导游提供关系" canManage={props.canManageCapability} onAdd={() => {
              setGuideRelationId(undefined);
              setGuideDraft(emptyGuideRelation(supplierId));
            }} />
            {guideDraft ? <GuideRelationEditor value={guideDraft} currentGuideName={item.supplierName} providerOptions={guideProviderQuery.data ?? []} saving={guideSave.isPending} onSearchProvider={setGuideProviderSearch} onChange={setGuideDraft} onCancel={() => setGuideDraft(null)} onSave={() => {
              if (!guideDraft.providerSupplierId.trim()) {
                Message.warning("请选择提供方供应商");
                return;
              }
              guideSave.mutate({ id: guideRelationId, payload: guideDraft });
            }} /> : null}
            <DataTable headers={["提供方", "导游主体", "关系类型", "合作状态", "审核", "操作"]} empty={guideQuery.isLoading ? "正在加载提供关系..." : "暂无导游提供关系"}>
              {(guideQuery.data ?? []).map((relation) => (
                <tr key={relation.supplierGuideProviderRelationId}>
                  <td>{providerDisplay(guideProviderQuery.data ?? [], relation.providerSupplierId)}</td>
                  <td>{item.supplierName}（{relation.guideSupplierId}）</td>
                  <td>{relationTypeLabel(relation.relationType)}</td>
                  <td><StatusTag value={relation.cooperationStatus} /></td>
                  <td>{statusLabel(relation.reviewStatus)}</td>
                  <td><RowActions>{props.canManageCapability ? <IconButton label="编辑导游提供关系" icon={<Pencil size={15} />} onClick={() => {
                    setGuideRelationId(relation.supplierGuideProviderRelationId);
                    setGuideDraft({ ...relation });
                    setGuideProviderSearch(relation.providerSupplierId);
                  }} /> : <span className="supply-basic-muted">只读</span>}</RowActions></td>
                </tr>
              ))}
            </DataTable>
          </Tabs.TabPane>
        ) : null}

        {kind === "hotels" ? (
          <Tabs.TabPane key="stay-capabilities" title={`住宿供应能力 ${stayQuery.data?.length ?? 0}`}>
            <div className="supply-basic-readonly-banner">住宿供应关系由住宿资源域维护，此处只读。</div>
            <DataTable headers={["住宿资源", "SKU", "采购渠道", "合作状态", "有效期", "审核"]} empty={stayQuery.isLoading ? "正在加载住宿供应能力..." : "暂无住宿供应能力"}>
              {(stayQuery.data ?? []).map((capability) => (
                <tr key={capability.resourceStaySupplierCapabilityId}>
                  <td>{capability.hotelName || capability.staySpuId}</td>
                  <td>{capability.staySkuId || "全部房型"}</td>
                  <td>{capability.procurementChannelType}</td>
                  <td><StatusTag value={capability.cooperationStatus} /></td>
                  <td>{[capability.validFrom, capability.validTo].filter(Boolean).join(" 至 ") || "-"}</td>
                  <td>{statusLabel(capability.reviewStatus)}</td>
                </tr>
              ))}
            </DataTable>
          </Tabs.TabPane>
        ) : null}
      </Tabs>
    </Drawer>
  );
}

function SectionToolbar({ label, canManage, onAdd }: { label: string; canManage: boolean; onAdd: () => void }) {
  return <div className="supply-basic-section-toolbar"><h3>{label}</h3>{canManage ? <Button type="primary" icon={<Plus size={15} />} onClick={onAdd}>新增</Button> : null}</div>;
}

function DataTable({ headers, empty, children }: { headers: string[]; empty: string; children: ReactNode }) {
  const hasRows = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return <div className="supply-basic-table-scroll"><table className="supply-basic-table supply-basic-detail-table"><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{hasRows ? children : <tr><td colSpan={headers.length} className="supply-basic-empty">{empty}</td></tr>}</tbody></table></div>;
}

function RowActions({ children }: { children: ReactNode }) {
  return <div className="supply-basic-row-actions">{children}</div>;
}

function IconButton({ label, icon, onClick }: { label: string; icon: ReactNode; onClick: () => void }) {
  return <Button type="text" shape="circle" aria-label={label} title={label} icon={icon} onClick={onClick} />;
}

function StatusTag({ value }: { value: string }) {
  const positive = ["active", "valid", "approved", "verified", "enabled"].includes(value);
  const negative = ["inactive", "obsolete", "expired", "rejected", "terminated"].includes(value);
  return <Tag color={positive ? "green" : negative ? "orangered" : "arcoblue"}>{statusLabel(value)}</Tag>;
}

function EditorActions({ saving, onCancel, onSave }: { saving: boolean; onCancel: () => void; onSave: () => void }) {
  return <div className="supply-basic-editor-actions"><Button onClick={onCancel}>取消</Button><Button type="primary" loading={saving} icon={<Save size={15} />} onClick={onSave}>保存</Button></div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="supply-basic-detail-field"><span>{label}</span>{children}</label>;
}

export function ContactEditor({ value, saving, onChange, onCancel, onSave }: { value: SupplierContactMutationInput; saving: boolean; onChange: (value: SupplierContactMutationInput) => void; onCancel: () => void; onSave: () => void }) {
  return <div className="supply-basic-inline-editor"><div className="supply-basic-editor-grid">
    <Field label="姓名"><Input value={value.contactName} onChange={(contactName) => onChange({ ...value, contactName })} /></Field>
    <Field label="角色"><Select value={value.contactRoleType} options={[...supplierContactRoleOptions]} onChange={(contactRoleType) => onChange({ ...value, contactRoleType })} /></Field>
    <Field label="手机号"><Input value={value.mobile} onChange={(mobile) => onChange({ ...value, mobile })} /></Field>
    <Field label="电话"><Input value={value.phone} onChange={(phone) => onChange({ ...value, phone })} /></Field>
    <Field label="微信"><Input value={value.wechat} onChange={(wechat) => onChange({ ...value, wechat })} /></Field>
    <Field label="邮箱"><Input value={value.email} onChange={(email) => onChange({ ...value, email })} /></Field>
  </div><div className="supply-basic-checkbox-grid">{[
    ["报价联系人", "isDefaultQuoteContact"], ["结算联系人", "isDefaultSettlementContact"], ["运营联系人", "isDefaultOperationContact"], ["紧急联系人", "isDefaultEmergencyContact"],
  ].map(([label, key]) => <label key={key}><input type="checkbox" checked={Boolean(value[key as keyof SupplierContactMutationInput])} onChange={(event) => onChange({ ...value, [key]: event.target.checked })} />{label}</label>)}</div><EditorActions saving={saving} onCancel={onCancel} onSave={onSave} /></div>;
}

function AccountEditor({ value, editing, saving, onChange, onCancel, onSave }: { value: SupplierBankAccountMutation; editing: boolean; saving: boolean; onChange: (value: SupplierBankAccountMutation) => void; onCancel: () => void; onSave: () => void }) {
  return <div className="supply-basic-inline-editor"><div className="supply-basic-editor-grid">
    <Field label="账户户名"><Input value={value.accountName} onChange={(accountName) => onChange({ ...value, accountName })} /></Field>
    <Field label="开户行"><Input value={value.bankName} onChange={(bankName) => onChange({ ...value, bankName })} /></Field>
    <Field label="支行"><Input value={value.bankBranchName} onChange={(bankBranchName) => onChange({ ...value, bankBranchName })} /></Field>
    <Field label={editing ? "输入完整账户号以确认更新" : "账户号"}><Input value={value.bankAccountNo} onChange={(bankAccountNo) => onChange({ ...value, bankAccountNo })} /></Field>
    <Field label="账户类型"><Select value={value.accountType} options={[{ label: "对公", value: "public" }, { label: "个人", value: "private" }, { label: "其他", value: "other" }]} onChange={(accountType) => onChange({ ...value, accountType })} /></Field>
    <Field label="用途"><Select value={value.usageType} options={[{ label: "收款", value: "payee" }, { label: "退款", value: "refund" }, { label: "收款与退款", value: "both" }, { label: "其他", value: "other" }]} onChange={(usageType) => onChange({ ...value, usageType })} /></Field>
    <Field label="生效日期"><Input type="date" value={value.effectiveFrom} onChange={(effectiveFrom) => onChange({ ...value, effectiveFrom })} /></Field>
    <Field label="失效日期"><Input type="date" value={value.effectiveTo} onChange={(effectiveTo) => onChange({ ...value, effectiveTo })} /></Field>
  </div><div className="supply-basic-checkbox-grid"><label><input type="checkbox" checked={value.isDefaultPayee} onChange={(event) => onChange({ ...value, isDefaultPayee: event.target.checked })} />默认收款</label><label><input type="checkbox" checked={value.isDefaultRefundAccount} onChange={(event) => onChange({ ...value, isDefaultRefundAccount: event.target.checked })} />默认退款</label></div><EditorActions saving={saving} onCancel={onCancel} onSave={onSave} /></div>;
}

export function AttachmentEditor({ value, saving, onChange, onCancel, onUpload, onSave }: { value: SupplierAttachmentMutationInput; saving: boolean; onChange: (value: SupplierAttachmentMutationInput) => void; onCancel: () => void; onUpload: (file: File) => Promise<void>; onSave: () => void }) {
  return <div className="supply-basic-inline-editor"><div className="supply-basic-editor-grid">
    <Field label="附件类型"><Select value={value.attachmentType} options={[...supplierAttachmentTypeOptions]} onChange={(attachmentType) => onChange({ ...value, attachmentType })} /></Field>
    <Field label="附件名称"><Input value={value.attachmentName} onChange={(attachmentName) => onChange({ ...value, attachmentName })} /></Field>
    <Field label="有效期起"><Input type="date" value={value.effectiveFrom} onChange={(effectiveFrom) => onChange({ ...value, effectiveFrom })} /></Field>
    <Field label="有效期止"><Input type="date" value={value.effectiveTo} onChange={(effectiveTo) => onChange({ ...value, effectiveTo })} /></Field>
    <Field label="文件地址"><Input value={value.fileUrl} onChange={(fileUrl) => onChange({ ...value, fileUrl })} /></Field>
    <Field label="上传文件"><label className="supply-basic-file-button"><Upload size={15} /><span>选择文件</span><input type="file" accept="image/*,.pdf" onChange={(event) => { const file = event.target.files?.[0]; if (file) void onUpload(file); event.target.value = ""; }} /></label></Field>
  </div><EditorActions saving={saving} onCancel={onCancel} onSave={onSave} /></div>;
}

function QuoteEditor({ value, canManage, saving, onChange, onSave }: { value: SupplierQuoteCapabilityMutation; canManage: boolean; saving: boolean; onChange: (value: SupplierQuoteCapabilityMutation) => void; onSave: () => void }) {
  const capabilities = [
    ["酒店", "canQuoteHotel", "hotelAreaText"], ["车队", "canQuoteBus", "busAreaText"], ["门票", "canQuoteTicket", "ticketAreaText"], ["导游", "canQuoteGuide", "guideAreaText"], ["餐饮", "canQuoteMeal", "mealAreaText"],
  ] as const;
  return <div className="supply-basic-capability-editor"><div className="supply-basic-quote-grid">{capabilities.map(([label, toggleKey, areaKey]) => <div key={toggleKey}><label><input type="checkbox" disabled={!canManage} checked={value[toggleKey]} onChange={(event) => onChange({ ...value, [toggleKey]: event.target.checked })} />可报价{label}</label><Input disabled={!canManage || !value[toggleKey]} placeholder="服务区域" value={value[areaKey]} onChange={(text) => onChange({ ...value, [areaKey]: text })} /></div>)}</div><div className="supply-basic-editor-grid"><Field label="优先级"><Input type="number" disabled={!canManage} value={value.quotePriorityLevel?.toString()} onChange={(text) => onChange({ ...value, quotePriorityLevel: text ? Number(text) : undefined })} /></Field><Field label="能力说明"><Input.TextArea disabled={!canManage} value={value.capabilityRemark} onChange={(capabilityRemark) => onChange({ ...value, capabilityRemark })} /></Field></div>{canManage ? <div className="supply-basic-editor-actions"><Button type="primary" loading={saving} icon={<Save size={15} />} onClick={onSave}>保存报价能力</Button></div> : <div className="supply-basic-readonly-banner">当前账号可查看，但无能力维护权限。</div>}</div>;
}

function BusCapabilityEditor({ value, saving, onChange, onCancel, onSave }: { value: SupplierBusCapabilityMutation; saving: boolean; onChange: (value: SupplierBusCapabilityMutation) => void; onCancel: () => void; onSave: () => void }) {
  return <div className="supply-basic-inline-editor"><div className="supply-basic-editor-grid"><Field label="省份"><Input value={value.province} onChange={(province) => onChange({ ...value, province })} /></Field><Field label="城市"><Input value={value.city} onChange={(city) => onChange({ ...value, city })} /></Field><Field label="区县"><Input value={value.district} onChange={(district) => onChange({ ...value, district })} /></Field><Field label="车型"><Input value={value.vehicleType} onChange={(vehicleType) => onChange({ ...value, vehicleType })} /></Field><Field label="最小座位数"><Input type="number" value={value.seatCountMin?.toString()} onChange={(text) => onChange({ ...value, seatCountMin: text ? Number(text) : undefined })} /></Field><Field label="最大座位数"><Input type="number" value={value.seatCountMax?.toString()} onChange={(text) => onChange({ ...value, seatCountMax: text ? Number(text) : undefined })} /></Field><Field label="服务方式"><Input value={value.serviceMode} onChange={(serviceMode) => onChange({ ...value, serviceMode })} /></Field><TimedStatusFields value={value} onChange={onChange} /></div><EditorActions saving={saving} onCancel={onCancel} onSave={onSave} /></div>;
}

export function GuideRelationEditor({ value, currentGuideName, providerOptions, saving, onSearchProvider, onChange, onCancel, onSave }: { value: SupplierGuideRelationMutation; currentGuideName: string; providerOptions: GuideProviderOption[]; saving: boolean; onSearchProvider: (query: string) => void; onChange: (value: SupplierGuideRelationMutation) => void; onCancel: () => void; onSave: () => void }) {
  return <div className="supply-basic-inline-editor"><div className="supply-basic-editor-grid"><Field label="提供方供应商"><Select showSearch filterOption={false} value={value.providerSupplierId || undefined} options={providerOptions.map((option) => ({ label: `${option.supplierName}（${option.supplierTypeLabel}）`, value: option.supplierId }))} onSearch={onSearchProvider} onChange={(providerSupplierId) => onChange({ ...value, providerSupplierId })} /></Field><Field label="当前导游供应商 ID"><Input readOnly value={value.guideSupplierId} suffix={currentGuideName} /></Field><Field label="关系类型"><Select value={value.relationType} options={[{ label: "雇佣", value: "employment" }, { label: "代理", value: "agency" }, { label: "派遣合作", value: "dispatch_partner" }, { label: "其他", value: "other" }]} onChange={(relationType) => onChange({ ...value, relationType })} /></Field><TimedStatusFields value={value} onChange={onChange} /></div><EditorActions saving={saving} onCancel={onCancel} onSave={onSave} /></div>;
}

function TimedStatusFields<T extends SupplierBusCapabilityMutation | SupplierGuideRelationMutation>({ value, onChange }: { value: T; onChange: (value: T) => void }) {
  return <><Field label="合作状态"><Select value={value.cooperationStatus} options={[...supplierTimedCooperationStatusOptions]} onChange={(cooperationStatus) => onChange({ ...value, cooperationStatus } as T)} /></Field><Field label="审核状态"><Select value={value.reviewStatus} options={[...supplierTimedReviewStatusOptions]} onChange={(reviewStatus) => onChange({ ...value, reviewStatus } as T)} /></Field><Field label="有效期起"><Input type="date" value={value.validFrom} onChange={(validFrom) => onChange({ ...value, validFrom })} /></Field><Field label="有效期止"><Input type="date" value={value.validTo} onChange={(validTo) => onChange({ ...value, validTo })} /></Field><Field label="证据摘要"><Input value={value.evidenceSummary} onChange={(evidenceSummary) => onChange({ ...value, evidenceSummary })} /></Field><Field label="备注"><Input value={value.remark} onChange={(remark) => onChange({ ...value, remark })} /></Field></>;
}

function optionLabel(options: readonly { label: string; value: string }[], value: string) {
  return options.find((option) => option.value === value)?.label ?? value;
}

function statusLabel(value: string) {
  return optionLabel(
    [
      ...supplierTimedCooperationStatusOptions,
      ...supplierTimedReviewStatusOptions,
      { label: "待审核", value: "pending_review" },
      { label: "有效", value: "valid" },
      { label: "已过期", value: "expired" },
      { label: "已作废", value: "obsolete" },
      { label: "启用", value: "active" },
      { label: "停用", value: "inactive" },
    ],
    value,
  );
}

function relationTypeLabel(value: SupplierGuideRelationMutation["relationType"]) {
  return optionLabel([
    { label: "雇佣", value: "employment" },
    { label: "代理", value: "agency" },
    { label: "派遣合作", value: "dispatch_partner" },
    { label: "其他", value: "other" },
  ], value);
}

function providerDisplay(options: GuideProviderOption[], supplierId: string) {
  const option = options.find((item) => item.supplierId === supplierId);
  return option ? `${option.supplierName}（${option.supplierTypeLabel}）` : supplierId;
}

function contactDefaultLabels(contact: SupplierContactRecord) {
  const labels: string[] = [];
  if (contact.isDefaultQuoteContact) labels.push("报价");
  if (contact.isDefaultSettlementContact) labels.push("结算");
  if (contact.isDefaultOperationContact) labels.push("运营");
  if (contact.isDefaultEmergencyContact) labels.push("紧急");
  return labels;
}

function seatText(capability: SupplierBusCapabilityRecord) {
  if (capability.seatCountMin && capability.seatCountMax) return `${capability.seatCountMin}-${capability.seatCountMax} 座`;
  if (capability.seatCountMin) return `${capability.seatCountMin} 座起`;
  if (capability.seatCountMax) return `${capability.seatCountMax} 座以内`;
  return "";
}

function showMutationError(error: unknown) {
  Message.error(error instanceof Error ? error.message : "操作失败，请稍后重试");
}
